import React, { useMemo } from "react";
import Plot, { PLOT_CONFIG, PLOT_FONT } from "./plotly";
import { styleFor } from "../lib/conditions";

/**
 * Internal-standard peak area against injection order -- the chart the lab
 * currently assembles by hand in qc.pptx.
 *
 * The median and the +/- tolerance band are drawn so a drift or a spike is read
 * off the chart rather than inferred from the flag table.
 */
export default function QcChart({ trace, flaggedInjections = [] }) {
  const flagged = useMemo(() => new Set(flaggedInjections), [flaggedInjections]);
  const style = styleFor(trace.condition);

  const points = trace.injection_index.map((index, i) => ({
    index,
    name: trace.sample_name[i],
    area: trace.is_peak_area[i],
  }));

  const normal = points.filter((p) => !flagged.has(p.index));
  const outliers = points.filter((p) => flagged.has(p.index));

  const data = [
    {
      x: points.map((p) => p.index),
      // Solvent blanks carry no internal standard, so their near-zero areas are
      // expected. They stay on the chart as markers but are lifted out of the
      // trend line, which would otherwise dive to the axis at every blank and
      // hide the drift the chart exists to show.
      y: points.map((p) => (p.area ? p.area : null)),
      type: "scatter",
      mode: "lines",
      line: { color: style.color, width: 1 },
      connectgaps: false,
      hoverinfo: "skip",
      showlegend: false,
    },
    {
      x: normal.map((p) => p.index),
      y: normal.map((p) => p.area),
      customdata: normal.map((p) => p.name),
      type: "scatter",
      mode: "markers",
      marker: { color: style.color, size: 5 },
      name: "Injections",
      hovertemplate: "%{customdata}<br>injection %{x}<br>%{y:,.0f} counts<extra></extra>",
    },
  ];

  if (outliers.length) {
    data.push({
      x: outliers.map((p) => p.index),
      y: outliers.map((p) => p.area),
      customdata: outliers.map((p) => p.name),
      type: "scatter",
      mode: "markers",
      marker: { color: "#b3261e", size: 10, symbol: "x", line: { width: 1 } },
      name: "Flagged",
      hovertemplate: "%{customdata}<br>injection %{x}<br>%{y:,.0f} counts<extra>flagged</extra>",
    });
  }

  const shapes = [];
  if (trace.median != null) {
    shapes.push({
      type: "line",
      xref: "paper",
      x0: 0,
      x1: 1,
      y0: trace.median,
      y1: trace.median,
      line: { color: "#6b7280", width: 1, dash: "dash" },
    });
  }
  if (trace.lower_limit != null && trace.upper_limit != null) {
    shapes.push({
      type: "rect",
      xref: "paper",
      x0: 0,
      x1: 1,
      y0: trace.lower_limit,
      y1: trace.upper_limit,
      fillcolor: "#6b7280",
      opacity: 0.07,
      line: { width: 0 },
      layer: "below",
    });
  }

  return (
    <Plot
      data={data}
      layout={{
        title: { text: trace.condition, font: { size: 13 } },
        height: 260,
        margin: { l: 70, r: 20, t: 36, b: 44 },
        xaxis: { title: "Injection order", gridcolor: "#eef0f2", zeroline: false },
        yaxis: {
          title: "IS peak area [counts]",
          gridcolor: "#eef0f2",
          rangemode: "tozero",
          tickformat: ".2s",
        },
        shapes,
        font: PLOT_FONT,
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        legend: { orientation: "h", y: -0.28 },
        hovermode: "closest",
      }}
      config={PLOT_CONFIG}
      style={{ width: "100%" }}
      useResizeHandler
    />
  );
}
